import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, firstValueFrom, filter } from 'rxjs';
import axios from 'axios';
import { AuthService } from 'src/app/shared/services/auth/auth.service';
import { environment } from 'src/environments/environment';
import { LawFirmItem } from '../law-firm/law-firm.service';

export type GetUserResponse = {
  name: string;
  can_update_product_price: boolean;
  email: string;
  digest_notifications: boolean;
  has_2fa: boolean;
};

export type UserDataTableItem = {
  user_uuid: string;
  email: string;
  user_name: string;
  digest_notifications: boolean;
  digest_notifications_exists: boolean;
  roles: {
    name: string;
  }[];
  attorney_countries: {
    iso: string;
    name: string;
  }[];
  phone_number: string | null;
  clients: {
    name: string;
    uuid: string;
  }[];
  created_at: string;
  law_firms: LawFirmItem[];
};

export type UserDataTableParams = {
  user_email?: string;
  user_name?: string;
  role_name?: string;
  client_id?: string;
  page?: number;
  per_page?: number;
  sort_by?: keyof UserDataTableItem;
  sort_direction?: 'asc' | 'desc';
};

export type UserDataTableHeader = {
  title: string;
  align: 'start' | 'center';
  key?: keyof UserDataTableItem;
};

export type UserDataTableResponse = {
  headers: UserDataTableHeader[];
  sortBy: unknown[];
  data: UserDataTableItem[];
  pagination: {
    currentPage: number;
    perPage: number;
    total: number;
    lastPage: number;
  };
};

export type CreateUserParams = {
  email: string;
  name: string;
  phone?: string;
  roles: string[];
  attorney_countries: string[];
  client_ids: string[];
  law_firms: number[];
};

export type UpdateUserParams = {
  email?: string;
  name?: string;
  phone?: string;
  roles?: string[];
  client_ids?: string[];
  attorney_countries?: string[];
  digest_notifications?: boolean;
  law_firms?: number[];
};

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  private authService = inject(AuthService);
  readonly userSubject = new BehaviorSubject<GetUserResponse | null>(null);

  constructor() {
    this.authService.loggedOut$
      .subscribe(() => {
        this.userSubject.next(null);
      });
  }

  async getUser(): Promise<GetUserResponse> {
    return await firstValueFrom(this.userSubject.pipe(
      filter((user): user is GetUserResponse => !!user),
    ));
  }

  async getUsersDataTable(params: UserDataTableParams): Promise<UserDataTableResponse> {
    const url = environment.baseUrl + '/api/user-datatable';
    const result = await axios.get<UserDataTableResponse>(
      url,
      {
        params,
      },
    );

    return result.data;
  }

  async createUser(params: CreateUserParams): Promise<GetUserResponse> {
    const url = environment.baseUrl + `/api/user`;
    const result = await axios.post<GetUserResponse>(url, params);
    return result.data;
  }

  async updateUser(uuid: string, params: UpdateUserParams): Promise<GetUserResponse> {
    const url = environment.baseUrl + `/api/user/${uuid}`;
    const result = await axios.post<GetUserResponse>(url, {
      ...params,
      '_method': 'PUT',
    });
    return result.data;
  }

  async deleteUser(uuid: string): Promise<void> {
    const url = environment.baseUrl + `/api/user/${uuid}`;
    await axios.delete<GetUserResponse>(url);
  }

  async sendDigestNotifications(uuid: string): Promise<void> {
    const url = environment.baseUrl + `/api/user/${uuid}/send-digest-notifications`;
    await axios.post<GetUserResponse>(url);
  }

  async setUser(): Promise<GetUserResponse> {
    const url = environment.baseUrl + `/api/user`;
    const result = await axios.get<GetUserResponse>(url);
    this.userSubject.next(result.data);
    return result.data;
  }
}
